import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { IconButton, LinkIconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import DepartmentIcon from '@atlaskit/icon/core/department';
import { Inline } from '@atlaskit/primitives/compiled';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/navigation';

const messages = defineMessages({
	viewOrgChart: {
		id: 'user-inline-preview.action-buttons.view-org-chart',
		defaultMessage: 'View org chart for {name}',
		description: 'Accessible label for the org chart icon button',
	},
	sendMessage: {
		id: 'user-inline-preview.action-buttons.send-message',
		defaultMessage: 'Send message to {name}',
		description: 'Accessible label for the send message button',
	},
	viewProfile: {
		id: 'user-inline-preview.action-buttons.view-profile',
		defaultMessage: 'View profile for {name}',
		description: 'Accessible label for the navigate to profile button',
	},
});

const styles = cssMap({
	actions: {
		opacity: 0,
	},
	actionsVisible: {
		opacity: 1,
	},
});

export interface ActionButtonsProps {
	name: string;
	accountId: string;
	email?: string | null;
	onViewOrgChart?: (accountId: string) => void;
	isVisible: boolean;
	cloudId: string;
}

/**
 * Internal component that renders action buttons (org chart, send message, view profile).
 * Shows each button when the corresponding data and handler are available.
 */
function ActionButtons({
	name,
	accountId,
	email,
	onViewOrgChart,
	isVisible,
	cloudId,
}: ActionButtonsProps) {
	const intl = useIntl();
	const { href, target, onNavigate } = navigateToTeamsApp({
		type: 'USER',
		payload: { userId: accountId },
		cloudId: cloudId ?? '',
	});

	return (
		<Inline
			space="space.100"
			alignBlock="center"
			xcss={cx(styles.actions, isVisible && styles.actionsVisible)}
		>
			{onViewOrgChart && (
				<IconButton
					icon={DepartmentIcon}
					label={intl.formatMessage(messages.viewOrgChart, { name })}
					onClick={() => onViewOrgChart(accountId)}
				/>
			)}
			{accountId && href && (
				<LinkIconButton
					href={href}
					target={target}
					onClick={onNavigate}
					icon={ArrowRightIcon}
					label={intl.formatMessage(messages.viewProfile, { name })}
					rel={target === '_blank' ? 'noopener noreferrer' : undefined}
				/>
			)}
		</Inline>
	);
}

export { ActionButtons };
